import { useState, type ReactNode } from 'react';

import { Button } from '../../button';

import {
  CEILINGS,
  changeLabels,
  charactersText,
  COMPARE_GROUPS,
  compareGroupLabels,
  compareRows,
  compareSummary,
  compareTotals,
  COMPARISONS,
  comparisonLabels,
  comparisonVersions,
  EMPTY_GROUP,
  graderText,
  groupRows,
  initialCompare,
  initialLatency,
  initialRegression,
  initialSettings,
  LATENCY_SQL,
  latencyBars,
  latencyStats,
  latencySummary,
  latencyVersionLabels,
  markHeight,
  noteTitle,
  onOff,
  PROMPT_VERSIONS,
  REGRESSION_NOTE_IDS,
  regressionOutcome,
  regressionSummary,
  settingsReading,
  settingsSummary,
  settingsText,
  STATISTICS,
  statisticLabels,
  statisticLine,
  TEMPERATURES,
  TITLE_LIMITS,
  toCeiling,
  toCompareGroup,
  toComparison,
  toPromptVersion,
  toRegressionNoteId,
  toStatistic,
  toTemperature,
  toTitleLimit,
} from './experiment-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './experiment-labs.css';

function Predict({ children }: { children: ReactNode }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Toggle({
  label,
  pressed,
  onClick,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={pressed ? 'primary' : 'secondary'}
      aria-pressed={pressed}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
function Table({
  label,
  columns,
  rows,
  template,
  empty = 'No rows',
}: {
  label: string;
  columns: string[];
  rows: string[][];
  template: string;
  empty?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column} data-null={cell === '' ? 'true' : undefined}>
                  {cell === '' ? '—' : cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">{empty}</span>
          </div>
        )}
      </div>
    </div>
  );
}
const REPLAY = 'This graphic replays real runs.';

export function ExperimentCompareLab() {
  const [state, setState] = useState(initialCompare);
  const [before, after] = comparisonVersions(state.comparison);
  const rows = groupRows(compareRows(state.comparison), state.group);
  const summary = compareSummary(state);
  return (
    <Lab
      title="What did the new prompt fix, and what did it break?"
      eyebrow="Experiment · two runs side by side"
      className="rel-lab experiment-lab"
    >
      <Predict>Can a pass count that rose by three hide a broken note? Set Show to Broken.</Predict>
      <div className="cb-controls">
        <label>
          Compare
          <ModelSelect
            aria-label="Compare"
            value={state.comparison}
            onValueChange={(value) =>
              setState((current) => ({ ...current, comparison: toComparison(value) }))
            }
            options={COMPARISONS.map((comparison) => ({
              value: comparison,
              label: comparisonLabels[comparison],
            }))}
          />
        </label>
        <label>
          Show
          <ModelSelect
            aria-label="Show"
            value={state.group}
            onValueChange={(value) =>
              setState((current) => ({ ...current, group: toCompareGroup(value) }))
            }
            options={COMPARE_GROUPS.map((group) => ({
              value: group,
              label: compareGroupLabels[group],
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialCompare())} />
      </div>
      <Table
        label="Notes"
        columns={['Note', 'Title', `Version ${before}`, `Version ${after}`, 'Change', 'Reason']}
        rows={rows.map((row) => [
          String(row.id),
          row.title,
          row.before,
          row.after,
          changeLabels[row.change],
          row.reason,
        ])}
        template="52px minmax(130px, 1fr) 72px 72px 90px minmax(200px, 2fr)"
        empty={EMPTY_GROUP}
      />
      <Panel label="Totals">
        <pre aria-label="Totals">{compareTotals(state.comparison)}</pre>
      </Panel>
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ExperimentRegressionLab() {
  const [state, setState] = useState(initialRegression);
  const outcome = regressionOutcome(state);
  const summary = regressionSummary(state);
  return (
    <Lab
      title="Why did a rule about conditions break the invoice note?"
      eyebrow="Experiment · one regression, three facts"
      className="rel-lab experiment-lab"
    >
      <Predict>
        Will the title still fit in 80 characters? Press Keep the condition in the title (version 2
        rule).
      </Predict>
      <div className="cb-controls">
        <label>
          Note
          <ModelSelect
            aria-label="Note"
            value={String(state.note)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, note: toRegressionNoteId(value) }))
            }
            options={REGRESSION_NOTE_IDS.map((id) => ({
              value: String(id),
              label: `Note ${id}: ${noteTitle(id)}`,
            }))}
          />
        </label>
        <label>
          Title limit
          <ModelSelect
            aria-label="Title limit"
            value={String(state.limit)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, limit: toTitleLimit(value) }))
            }
            options={TITLE_LIMITS.map((limit) => ({
              value: String(limit),
              label: `${limit} characters`,
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialRegression())} />
      </div>
      <div className="cb-controls">
        <Toggle
          label="Keep the condition in the title (version 2 rule)"
          pressed={state.keepCondition}
          onClick={() =>
            setState((current) => ({ ...current, keepCondition: !current.keepCondition }))
          }
        />
        <Toggle
          label="Shorten, never split (version 3 rule)"
          pressed={state.shorten}
          onClick={() => setState((current) => ({ ...current, shorten: !current.shorten }))}
        />
      </div>
      <Table
        label="Titles"
        columns={['Title the model produced', 'Characters']}
        rows={outcome.titles.map((row) => [row.title, charactersText(row)])}
        template="minmax(220px, 3fr) minmax(130px, 1fr)"
      />
      <Panel label="Grader">
        <pre aria-label="Grader">{graderText(outcome)}</pre>
      </Panel>
      <Facts
        values={[
          ['Version 2 rule', onOff(state.keepCondition)],
          ['Version 3 rule', onOff(state.shorten)],
          ['Verdict', outcome.passed ? 'pass' : 'fail'],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ExperimentSettingsLab() {
  const [state, setState] = useState(initialSettings);
  const reading = settingsReading(state);
  const summary = settingsSummary(state);
  return (
    <Lab
      title="Which setting changes the result, and which changes the bill?"
      eyebrow="Experiment · one setting at a time"
      className="rel-lab experiment-lab"
    >
      <Predict>
        Which setting moves the pass count, and which moves the cost? Change Prompt version,
        Temperature or Ceiling (max_tokens), one at a time.
      </Predict>
      <div className="cb-controls">
        <label>
          Prompt version
          <ModelSelect
            aria-label="Prompt version"
            value={String(state.version)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, version: toPromptVersion(value) }))
            }
            options={PROMPT_VERSIONS.map((version) => ({
              value: String(version),
              label: `Version ${version}`,
            }))}
          />
        </label>
        <label>
          Temperature
          <ModelSelect
            aria-label="Temperature"
            value={String(state.temperature)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, temperature: toTemperature(value) }))
            }
            options={TEMPERATURES.map((temperature) => ({
              value: String(temperature),
              label: String(temperature),
            }))}
          />
        </label>
        <label>
          Ceiling (max_tokens)
          <ModelSelect
            aria-label="Ceiling (max_tokens)"
            value={String(state.ceiling)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, ceiling: toCeiling(value) }))
            }
            options={CEILINGS.map((ceiling) => ({
              value: String(ceiling),
              label: String(ceiling),
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialSettings())} />
      </div>
      <Panel label="Settings">
        <pre aria-label="Settings">{settingsText(state)}</pre>
      </Panel>
      <Facts
        values={[
          ['Pass count', reading.passLine],
          ['Prompt tokens per call', String(reading.promptTokens)],
          ['Cost per 30-note run', reading.costLine],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ExperimentLatencyLab() {
  const [state, setState] = useState(initialLatency);
  const stats = latencyStats(state.version);
  const bars = latencyBars(state);
  const summary = latencySummary(state);
  return (
    <Lab
      title="What does one run cost in time, and which number should you quote?"
      eyebrow="Experiment · reading the latency log"
      className="rel-lab experiment-lab"
    >
      <Predict>Is p50 higher or lower than the mean? Set Statistic to p50.</Predict>
      <div className="cb-controls">
        <label>
          Version
          <ModelSelect
            aria-label="Version"
            value={String(state.version)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, version: toPromptVersion(value) }))
            }
            options={PROMPT_VERSIONS.map((version) => ({
              value: String(version),
              label: latencyVersionLabels[version],
            }))}
          />
        </label>
        <label>
          Statistic
          <ModelSelect
            aria-label="Statistic"
            value={state.statistic}
            onValueChange={(value) =>
              setState((current) => ({ ...current, statistic: toStatistic(value) }))
            }
            options={STATISTICS.map((statistic) => ({
              value: statistic,
              label: statisticLabels[statistic],
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialLatency())} />
      </div>
      <div
        className="experiment-strip"
        role="img"
        aria-label={`${stats.calls} calls sorted by latency; ${statisticLabels[state.statistic]} marked at ${statisticLine(state).split(' (')[0]}`}
      >
        {bars.map((bar, index) => (
          <span
            key={index}
            data-marked={bar.marked ? 'true' : undefined}
            style={{ height: `${bar.height}%` }}
          />
        ))}
        <div className="experiment-mark" style={{ bottom: `${markHeight(state)}%` }} />
      </div>
      <Facts
        values={[
          ['Mean', `${stats.mean} ms`],
          ['p50', `${stats.p50} ms`],
          ['p95', `${stats.p95} ms`],
        ]}
      />
      <div className="cb-two">
        <Panel label={statisticLabels[state.statistic]}>
          <pre aria-label="Statistic">{statisticLine(state)}</pre>
        </Panel>
        <Panel label="The query the lesson teaches">
          <pre aria-label="SQL">{LATENCY_SQL}</pre>
        </Panel>
      </div>
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
